/**
 * LP-403: what the quote builder screen holds while a partner reviews a meeting ("Found in the meeting"
 * and "From the web"). Plain functions, no imports: the page and the tests run the same code.
 *
 * A review is POST /api/copilot/meeting's answer. Each row has a default choice: 'accept' or 'skip'
 * ('edit' takes the partner's own text). Nothing goes into the quote until a partner presses Accept:
 * acceptedRows() is exactly what is sent, and only the rows the partner kept.
 */

/**
 * The meeting stays out of the Copilot chat (pre-arming). The chat (POST /api/copilot/chat) has tools, so
 * no transcript-derived text goes into the history it is sent. Every dock message that comes from a meeting
 * (the "Start from this meeting" line, the Copilot's summary of it, an error reading it, and what the page
 * says after an Accept) is made with meetingTurn(), which tags it intake: 'meeting' and makes it local: the
 * dock's request (dock-state.mjs dockRequest, LP-443) never sends a local message as history. The server
 * drops a history turn so tagged too (modules/copilot/routes.js).
 */
export const MEETING_INTAKE = 'meeting';

/** A dock message from a meeting (extra: the message's other fields, such as its version's thread, its change and Undo); local, so never history. */
export function meetingTurn(role, content, extra = {}) {
  return { ...extra, role, content, intake: MEETING_INTAKE, local: true };
}

/**
 * #50 fix round: the exact line a printing row puts in the scope block, for the value as read or as the partner
 * edited it (null for a row that does not print, or with no text). The page's copy of quote-scope.js meetingLine()
 * and oneLine() (the page cannot load that CommonJS file): one line, NEL and the line and paragraph separators
 * collapsed, control and format characters and lone surrogates dropped, cut to 300 characters by code point, the end stop dropped; the
 * mix up to its first ';'. A test keeps the two the same.
 */
const PRINT_LABELS = Object.freeze({ use: 'Use:', delivery: 'Delivery details:', timing: 'Timing:', mix: 'Based on the mix design provided:' });
export function printedLine(key, value) {
  const label = PRINT_LABELS[key];
  if (!label || typeof value !== 'string') return null;
  const one = (key === 'mix' ? value.split(';')[0] : value).replace(/[\s\u0085\u2028\u2029]+/g, ' ').replace(/[\p{Cc}\p{Cf}\p{Cs}]/gu, '').replace(/ {2,}/g, ' ').trim();
  const v = Array.from(one).slice(0, 300).join('').replace(/[\s.,;:]+$/, '');
  return v ? `${label} ${v}.` : null;
}

/** The line a row prints now: its value, or the partner's edit while Edit is picked (as acceptedRows() sends it). */
export function rowPrints(r, how, edits = {}) {
  const edited = how === 'edit' && String(edits[r?.key] ?? '').trim() ? String(edits[r.key]).trim() : null;
  return printedLine(r?.key, edited ?? r?.value);
}

/** Is "Start from a meeting" switched on for this quote (GET /api/quotes/:id says so only when it is)? */
export function meetingIntakeOn(data) {
  return data?.meeting_intake === true;
}

/**
 * The choices a review starts with: each row's default; rows with nothing to use have none, nor do the
 * rows whose figure is a pricing input of a blank-quote draft (r.pricing: picked under pricingPicks).
 */
export function initialChoices(rows) {
  return Object.fromEntries((rows || []).filter((r) => !r.none && !r.pricing).map((r) => [r.key, r.default === 'skip' ? 'skip' : 'accept']));
}

/** The rows a partner kept, as Accept sends them: the value read, or the partner's edit. */
export function acceptedRows(rows, choices = {}, edits = {}) {
  const out = [];
  for (const r of rows || []) {
    if (r.none || r.pricing) continue;
    const how = choices[r.key] || 'skip';
    if (how === 'skip') continue;
    const value = how === 'edit' && String(edits[r.key] ?? '').trim() ? String(edits[r.key]).trim() : r.value;
    const row = { key: r.key, value };
    if (r.offer_type) row.offer_type = r.offer_type;
    if (r.choice) row.choice = r.choice;
    if (r.source?.url) row.source_url = r.source.url;
    out.push(row);
  }
  return out;
}

/** How many rows are checked to go in, of the rows that can. */
export function checkedCount(rows, choices = {}) {
  const usable = (rows || []).filter((r) => !r.none && !r.pricing);
  return { checked: usable.filter((r) => (choices[r.key] || 'skip') !== 'skip').length, of: usable.length };
}

/**
 * What "Save to CRM" would save, from the facts the partner kept: the company (its name, phone, website
 * and HQ address from the web, each one checked) and the contact named in the meeting (name and role
 * only: nothing about a person comes from the web). null when there is nothing to save.
 */
export function crmDraft(review, { meeting = {}, web = {}, edits = {} } = {}) {
  const crmRow = (review?.web?.rows || []).find((r) => r.key === 'crm');
  if (!crmRow?.save || (!crmRow.save.account && !crmRow.save.contact)) return null;
  const kept = (rows, choices) => Object.fromEntries(acceptedRows(rows, choices, edits).map((r) => [r.key, r.value]));
  const m = kept(review?.meeting?.rows, meeting);
  const w = kept(review?.web?.rows, web);
  const name = w.company_name || m.customer || review?.crm?.account?.name || '';
  if (!name) return null;
  const contactText = m.contact || '';
  const [contactName, ...role] = contactText.split(',');
  return {
    account: { name, phone: w.main_phone || '', website: w.website || '', hq_address: w.hq_address || '' },
    contact: crmRow.save.contact && contactName.trim() ? { name: contactName.trim(), title: role.join(',').trim() } : null,
    creates: { account: !!crmRow.save.account, contact: !!(crmRow.save.contact && contactName.trim()) },
  };
}

/**
 * #48: a blank-quote draft's pricing inputs (review.pricing). Each needs a pick before the draft: 'meeting'
 * (the meeting's value), 'edit' (the partner's own, in edits under priceEditKey(key)) or 'default' (the
 * Copilot default the review shows, sent back with its token so a default that changed is caught). There
 * is no Skip. pricingPicks() is exactly what Accept sends, for the inputs picked.
 */
export const priceEditKey = (key) => `price:${key}`;

export function pricingPicks(review, choices = {}, edits = {}) {
  const out = [];
  for (const r of review?.pricing || []) {
    const how = choices[r.key];
    const typed = String(edits[priceEditKey(r.key)] ?? '').trim();
    if (how === 'meeting' && r.meeting?.value) out.push({ key: r.key, value: r.meeting.value });
    else if (how === 'edit' && typed) out.push({ key: r.key, value: typed });
    else if (how === 'default' && r.default) out.push({ key: r.key, use_default: true, seen: r.default.token });
  }
  return out;
}

/** The labels of the pricing inputs not picked yet (the draft waits for them). */
export function pricingMissing(review, choices = {}, edits = {}) {
  const picked = new Set(pricingPicks(review, choices, edits).map((x) => x.key));
  return (review?.pricing || []).filter((r) => !picked.has(r.key)).map((r) => r.label);
}

/**
 * After a draft refused for its pricing inputs (400 or 409, with the Copilot's defaults as they are now):
 * the review shows those defaults, and a 'default' pick whose default changed is cleared, to be picked again.
 */
export function withFreshDefaults(review, defaults, choices = {}) {
  if (!review?.pricing || !defaults) return { review, choices };
  const next = { ...choices };
  const pricing = review.pricing.map((r) => {
    const d = defaults[r.key] || null;
    if (next[r.key] === 'default' && (!d || r.default?.token !== d.token)) delete next[r.key];
    const row = { ...r, default: d };
    if (d) delete row.why;
    return row;
  });
  return { review: { ...review, pricing }, choices: next };
}
